import { useCallback } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { openURLInNewTab } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";
import { reverse } from "utils/urls";

import Text from "components/Text";
import toast from "components/Toast";

import { useOpenAIPromptResponseReady } from "entities/AISummaries/hooks";
import {
  useMemoPDFGenerationReadyEvent,
  useProformaPDFGenerationReadyEvent,
} from "entities/Deal/hooks";
import { memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve } from "entities/Memo/sdk";
import {
  useBulkDealImportReady,
  useChecklistFileImportReady,
  useCompanyPackageSnapshotProcessing,
  useCompanySectionAutomaticFillProcessing,
  useDealCompanyImportTableSheetNameNotFound,
  useDealCompanyImportTableUpdateFailed,
  useDealTableUpdateSucceeded,
  useMemoSectionAIExtractionProcessing,
  useTableOrDealTableUpdateFailed,
  useTableResponseReady,
  useTableUpdateSucceeded,
  useZipProcessing,
} from "entities/TableStorage/hooks";

const GlobalNotifications = () => {
  const navigate = useNavigate();

  // openAI prompt - response
  const openAIPromptResponseReadyEventHandler = useCallback(
    ({
      dealId,
      openAIDocumentAssistantId,
      promptResponseId,
    }: {
      dealId: number;
      openAIDocumentAssistantId: number;
      promptResponseId: number;
    }) => {
      toast.successMessage(
        <Text variant="text2">
          Prompt response has been generated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                `${reverse(URLS.DEAL_AI_SUMMARIES_EDITOR, {
                  dealId,
                  openAIDocumentAssistantId,
                })}?prompt-response-id=${promptResponseId}`
              )
            }
          >
            here
          </Text>{" "}
          to view the answer.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useOpenAIPromptResponseReady({
    eventHandler: openAIPromptResponseReadyEventHandler,
  });

  const tableResponseSuccessEventHandler = useCallback(
    ({ tableResponseId }: { tableResponseId: number }) => {
      toast.successMessage(
        <Text variant="text2">
          Table report has been generated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.TABLES_RESPONSE_DETAIL, {
                  tableResponseId,
                })
              )
            }
          >
            here
          </Text>{" "}
          to view the report.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  const tableResponseFailedEventHandler = useCallback(
    () => toast.errorMessage("Table report failed. Please try again."),
    []
  );

  useTableResponseReady({
    successEventHandler: tableResponseSuccessEventHandler,
    failureEventHandler: tableResponseFailedEventHandler,
  });

  const bulkDealImportSuccessEventHandler = useCallback(() => {
    toast.successMessage(
      <Text variant="text2">
        All deals have been imported successfully! Click{" "}
        <Text
          component="span"
          variant="text2"
          color="primary"
          onClick={() =>
            navigate(
              reverse(URLS.PROJECTS_LIST, {
                phase: "All",
              })
            )
          }
        >
          here
        </Text>{" "}
        to view the deal dashboard.
      </Text>,
      { autoClose: false }
    );
  }, [navigate]);

  const bulkDealImportFailedEventHandler = useCallback(
    ({ detail }: { detail: string | Record<string, string[]> }) => {
      if (typeof detail === "string") {
        // Handle plain string errors
        toast.errorMessage(detail, {
          autoClose: false,
        });
      } else if (typeof detail === "object" && !_.isNil(detail)) {
        // Handle JSON object errors
        Object.entries(detail).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach((error) => {
              toast.errorMessage(
                `An error occured in ${field.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase())}: ${error}`,
                {
                  autoClose: false,
                }
              );
            });
          } else {
            toast.errorMessage(`An error occured in ${field}: ${errors}`, {
              autoClose: false,
            });
          }
        });
      } else {
        toast.errorMessage("An unexpected error occurred.", {
          autoClose: false,
        });
      }
    },
    []
  );

  useBulkDealImportReady({
    successEventHandler: bulkDealImportSuccessEventHandler,
    failureEventHandler: bulkDealImportFailedEventHandler,
  });

  const checklistFileImportCreateFailedEventHandler = useCallback(
    ({ details }) => {
      toast.errorMessage(details, { autoClose: false });
    },
    []
  );

  useChecklistFileImportReady({
    failureEventHandler: checklistFileImportCreateFailedEventHandler,
  });

  const tableOrDealTableUpdateFailedEventHandler = useCallback(
    ({ details }) => {
      return toast.errorMessage(details, { autoClose: false });
    },
    []
  );

  const companySectionAutomaticFillProcessingSucceededEventHandler =
    useCallback(
      ({
        dealId,
        packageId,
        tabId,
        companySectionAutomaticFillId,
      }: {
        dealId: number;
        packageId: number;
        tabId: number;
        companySectionAutomaticFillId: number;
      }) => {
        toast.successMessage(
          <Text variant="text2" component="span">
            The data extraction process has successfully finished! Click{" "}
            <Text
              component="span"
              variant="text2"
              color="primary"
              onClick={() =>
                navigate(
                  reverse(URLS.DEAL_PACKAGE_EXTRACTED_DATA_REVIEW, {
                    dealId,
                    packageId,
                    tabId,
                    companySectionAutomaticFillId,
                  })
                )
              }
            >
              here
            </Text>{" "}
            to review the extracted data.
          </Text>,
          { autoClose: false }
        );
      },
      [navigate]
    );

  const companySectionAutomaticFillProcessingFailedEventHandler = useCallback(
    ({ error }) => {
      toast.errorMessage(error);
    },
    []
  );

  const memoSectionAIExtractionProcessingSucceededEventHandler = useCallback(
    ({
      dealId,
      memoId,
      memoSectionId,
    }: {
      dealId: number;
      memoId: number;
      memoSectionId: number;
    }) => {
      toast.successMessage(
        <>
          <Text variant="text2" component="span">
            The data summarization process has successfully finished! Click{" "}
            <Text
              component="span"
              variant="text2"
              color="primary"
              onClick={() =>
                navigate(
                  `${reverse(URLS.DEAL_MEMO, {
                    dealId,
                    memoId,
                  })}?memoSectionId=${memoSectionId}`
                )
              }
            >
              here
            </Text>{" "}
            to see the summarized data.
          </Text>
        </>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  const companyPackageSnapshotProcessingFailedEventHandler = useCallback(
    ({ detail }: { detail: string }) => {
      toast.errorMessage(detail);
    },
    []
  );

  const companyPackageSnapshotProcessingSucceededEventHandler =
    useCallback(() => {
      toast.successMessage(
        <Text variant="text2" component="span">
          The PDF generation process has successfully finished!
        </Text>
      );
    }, []);

  useCompanyPackageSnapshotProcessing({
    successEventHandler: companyPackageSnapshotProcessingSucceededEventHandler,
    failureEventHandler: companyPackageSnapshotProcessingFailedEventHandler,
  });

  const onZipFailure = useCallback(() => {
    toast.errorMessage(
      "Something went wrong with the creation of the ZIP file. Please try again"
    );
  }, []);

  const onZipSuccess = useCallback(() => {
    toast.successMessage("The ZIP file has been successfully created!");
  }, []);

  useZipProcessing({
    successEventHandler: onZipSuccess,
    failureEventHandler: onZipFailure,
  });

  const memoSectionAIExtractionProcessingFailedEventHandler = useCallback(
    ({ error }) => {
      toast.errorMessage(error);
    },
    []
  );

  useCompanySectionAutomaticFillProcessing({
    successEventHandler: ({
      dealId,
      packageId,
      tabId,
      companySectionAutomaticFillId,
    }) =>
      companySectionAutomaticFillProcessingSucceededEventHandler({
        dealId,
        packageId,
        tabId,
        companySectionAutomaticFillId,
      }),
    failureEventHandler:
      companySectionAutomaticFillProcessingFailedEventHandler,
  });

  useMemoSectionAIExtractionProcessing({
    successEventHandler: ({ dealId, memoId, memoSectionId }) =>
      memoSectionAIExtractionProcessingSucceededEventHandler({
        dealId,
        memoId,
        memoSectionId,
      }),
    failureEventHandler: memoSectionAIExtractionProcessingFailedEventHandler,
  });

  const dealTableUpdateSucceededEventHandler = useCallback(
    ({ dealId, tableId, tableName }) => {
      toast.successMessage(
        <Text variant="text2">
          Table "{tableName}" has been updated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.DEAL_TABLE_STORAGE_DETAILS, {
                  dealId,
                  tableId,
                })
              )
            }
          >
            here
          </Text>{" "}
          to view the changes.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useDealTableUpdateSucceeded({
    successEventHandler: dealTableUpdateSucceededEventHandler,
  });

  const tableUpdateSucceededEventHandler = useCallback(
    ({ tableId, tableName }) => {
      toast.successMessage(
        <Text variant="text2" component="span">
          Table "{tableName}" has been updated successfully! Click{" "}
          <Text
            component="span"
            variant="text2"
            color="primary"
            onClick={() =>
              navigate(
                reverse(URLS.TABLE_STORAGE_DETAILS, {
                  tableId,
                })
              )
            }
          >
            here
          </Text>{" "}
          to view the changes.
        </Text>,
        { autoClose: false }
      );
    },
    [navigate]
  );

  useTableUpdateSucceeded({
    successEventHandler: tableUpdateSucceededEventHandler,
  });

  useTableOrDealTableUpdateFailed({
    failureEventHandler: tableOrDealTableUpdateFailedEventHandler,
  });

  useDealCompanyImportTableSheetNameNotFound({
    eventHandler: ({ details }) =>
      toast.loading(details, {
        type: "info",
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        isLoading: false,
        closeButton: true,
      }),
  });

  useDealCompanyImportTableUpdateFailed({
    eventHandler: ({ details }) =>
      toast.errorMessage(details, { autoClose: false }),
  });

  const proformaPDFGenerationSucceededEventHandler = useCallback(() => {
    toast.successMessage(
      <Text variant="text2" component="span">
        The generation of the Pro Forma PDF file has succeeded.
      </Text>
    );
  }, []);

  const proformaPDFGenerationFailedEventHandler = useCallback(() => {
    toast.errorMessage(
      <Text variant="text2" component="span">
        The generation of the Pro Forma PDF file has failed.
      </Text>
    );
  }, []);

  useProformaPDFGenerationReadyEvent({
    successEventHandler: proformaPDFGenerationSucceededEventHandler,
    failureEventHandler: proformaPDFGenerationFailedEventHandler,
  });

  const memoPDFGenerationSucceededEventHandler = useCallback(
    ({
      dealId,
      memoId,
      memoPDFId,
      disableGlobalNotification,
    }: {
      dealId: number;
      memoId: number;
      memoPDFId: number;
      disableGlobalNotification: boolean;
    }) => {
      !disableGlobalNotification &&
        toast.successMessage(
          <Text variant="text2">
            The Memo PDF has been successfully generated! Click{" "}
            <Text
              component="span"
              variant="text2"
              color="primary"
              onClick={() =>
                memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
                  dealId,
                  memoId,
                  memoPDFId,
                })
                  .then(({ url }) => {
                    openURLInNewTab({ url });
                  })
                  .catch(handleInvalidRequest)
              }
            >
              here
            </Text>{" "}
            to see it.
          </Text>
        );
    },
    []
  );

  const memoPDFGenerationFailedEventHandler = useCallback(
    ({ detail }: { detail: string }) => {
      toast.errorMessage(
        <Text variant="text2" component="span">
          {detail}
        </Text>
      );
    },
    []
  );

  useMemoPDFGenerationReadyEvent({
    successEventHandler: memoPDFGenerationSucceededEventHandler,
    failureEventHandler: memoPDFGenerationFailedEventHandler,
  });

  return null;
};

export default GlobalNotifications;
